import React, { useState } from "react";
import { Link } from "react-router-dom";
import { HiArrowRight } from "react-icons/hi";
import { SectionHeading, Reveal } from "./ui";

/** Silicon lifecycle: each stage links to the Chiparama domain that owns it. */
const stages = [
  { n: "01", stage: "Specification", domain: "RTL & Architecture", id: "rtl-architecture", blurb: "Micro-architecture and IP integration set the foundation." },
  { n: "02", stage: "Design", domain: "RTL & Architecture", id: "rtl-architecture", blurb: "RTL development with CDC/lint and low-power design." },
  { n: "03", stage: "Verification", domain: "Design Verification", id: "design-verification", blurb: "UVM, formal/ABV and coverage closure at scale." },
  { n: "04", stage: "Test architecture", domain: "DFT", id: "dft", blurb: "Scan/ATPG and MBIST/LBIST built in from the start." },
  { n: "05", stage: "Implementation", domain: "Physical Design & STA", id: "physical-design-sta", blurb: "Synthesis through routing, timing closure and signoff." },
  { n: "06", stage: "Pre-silicon", domain: "FPGA & Emulation", id: "fpga-emulation", blurb: "Prototyping and emulation de-risk the tape-out." },
  { n: "07", stage: "Bring-up", domain: "Silicon & Embedded", id: "silicon-embedded", blurb: "Characterization, firmware, drivers and BSP." },
];

export default function Lifecycle() {
  const [active, setActive] = useState(2);
  const cur = stages[active];

  return (
    <section className="on-dark noise section relative overflow-hidden bg-navy-950 text-white">
      <div className="bg-dots-dark absolute inset-0 opacity-70 [mask-image:radial-gradient(ellipse_at_50%_40%,black,transparent_70%)]" aria-hidden="true" />
      <div className="container-x relative">
        <SectionHeading index="02" eyebrow="The silicon lifecycle" title="One team across every stage that matters." dark>
          From specification to bring-up, each stage has a named Chiparama domain behind it—so ownership
          never gets lost at a handoff.
        </SectionHeading>

        {/* Stage rail */}
        <Reveal>
          <ol className="relative grid gap-3 md:grid-cols-7 md:gap-0">
            <div className="absolute left-0 right-0 top-[1.15rem] hidden h-px bg-gradient-to-r from-brand-light/10 via-brand-light/60 to-brand-light/10 md:block" aria-hidden="true" />
            <svg className="absolute left-0 top-[1.1rem] hidden h-2 w-full md:block" aria-hidden="true">
              <line x1="0" y1="4" x2="100%" y2="4" stroke="#7FA0FF" strokeOpacity="0.9" strokeWidth="1.5" strokeDasharray="4 8" className="animate-flow" />
            </svg>
            {stages.map((s, i) => (
              <li key={s.n}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onClick={() => setActive(i)}
                  aria-pressed={i === active}
                  className="group relative flex w-full items-center gap-4 text-left md:block md:pr-3"
                >
                  <span
                    className={`relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border font-mono text-[11px] transition-all duration-300 ${
                      i === active
                        ? "border-brand-light bg-brand text-white shadow-glow"
                        : "border-white/20 bg-navy-900 text-slate-400 group-hover:border-brand-light/60"
                    }`}
                  >
                    {s.n}
                  </span>
                  <span
                    className={`block md:mt-6 font-display text-base font-semibold tracking-tight transition-colors md:text-lg ${
                      i === active ? "text-white" : "text-slate-400 group-hover:text-slate-200"
                    }`}
                  >
                    {s.stage}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </Reveal>

        {/* Detail panel */}
        <Reveal delay={100}>
          <div className="relative mt-12 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] p-8 backdrop-blur md:mt-16 md:p-12">
            <div className="glow-blue absolute -right-24 -top-24 h-72 w-72 opacity-50" aria-hidden="true" />
            <div className="relative grid items-end gap-8 md:grid-cols-[1fr_auto]">
              <div>
                <p className="eyebrow">Stage {cur.n} / 07</p>
                <h3 className="mt-3 font-display text-3xl font-semibold tracking-tight md:text-5xl">{cur.stage}</h3>
                <p className="mt-4 max-w-xl text-lg text-slate-300">{cur.blurb}</p>
                <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-signal" aria-hidden="true" />
                  Owned by {cur.domain}
                </p>
              </div>
              <Link to={`/services#${cur.id}`} className="btn-primary group self-start md:self-end">
                Explore {cur.domain}
                <HiArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
